import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import Button from "@/components/ui/button";
import Textarea from "@/components/ui/textarea";
import { formatDateLong } from "@/utils";
import { clientEducationDetailApi } from "../../services/client-education-detail.api";
import type {
  EducationCommentDto,
  EducationCommentsResponseDto,
} from "../../types/client-education-detail.types";
import styles from "./sections.module.scss";

interface CommentsSectionProps {
  educationId: string;
}

const COMMENT_LIMIT = 50;
const COMMENT_MAX_LENGTH = 2000;

function getCommentAuthor(comment: EducationCommentDto): string {
  const fullName = [comment.user?.firstName, comment.user?.lastName]
    .filter(Boolean)
    .join(" ")
    .trim();
  return fullName || comment.user?.id || "-";
}

export function CommentsSection({ educationId }: CommentsSectionProps) {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [commentsEnabled, setCommentsEnabled] = useState(false);
  const [items, setItems] = useState<EducationCommentDto[]>([]);
  const [content, setContent] = useState("");

  const remainingChars = useMemo(
    () => COMMENT_MAX_LENGTH - content.length,
    [content],
  );

  const loadComments = useCallback(async () => {
    setIsLoading(true);
    try {
      const response: EducationCommentsResponseDto =
        await clientEducationDetailApi.getComments(educationId, COMMENT_LIMIT);
      setCommentsEnabled(response.commentsEnabled);
      setItems(response.items ?? []);
      setIsVisible(true);
    } catch (error) {
      const status = (error as { response?: { status?: number } })?.response
        ?.status;
      if (status === 403) {
        setIsVisible(false);
      }
    } finally {
      setIsLoading(false);
    }
  }, [educationId]);

  useEffect(() => {
    setContent("");
    void loadComments();
  }, [loadComments]);

  if (!isVisible) return null;

  const isSubmitDisabled =
    isSubmitting ||
    isLoading ||
    !commentsEnabled ||
    content.trim().length === 0 ||
    content.length > COMMENT_MAX_LENGTH;

  const handleSubmit = async () => {
    const trimmed = content.trim();
    if (!trimmed || isSubmitDisabled) return;

    setIsSubmitting(true);
    try {
      const created = await clientEducationDetailApi.createComment(
        educationId,
        trimmed,
      );
      setItems((prev) => [...prev, created]);
      setContent("");
      toast.success(t("clientEducationDetail.comments.submitSuccess"));
    } catch (error) {
      const status = (error as { response?: { status?: number } })?.response
        ?.status;
      if (status === 403) {
        await loadComments();
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="comments" className={styles.section}>
      <div className={styles.header}>
        <h2>{t("clientEducationDetail.comments.title")}</h2>
        {!isLoading && (
          <span className={styles.headerCount}>{items.length}</span>
        )}
      </div>

      <div className={styles.commentsComposer}>
        <Textarea
          disabled={isLoading || isSubmitting || !commentsEnabled}
          maxLength={COMMENT_MAX_LENGTH}
          onChange={(event) => setContent(event.target.value)}
          placeholder={t("clientEducationDetail.comments.placeholder")}
          rows={4}
          value={content}
        />
        <div className={styles.commentsComposerFooter}>
          <div className={styles.commentsMetaBlock}>
            {!commentsEnabled && !isLoading && (
              <span className={styles.commentsInfoMuted}>
                {t("clientEducationDetail.comments.disabled")}
              </span>
            )}
            <span
              className={styles.commentsCounter}
              data-invalid={remainingChars < 0 || undefined}
            >
              {t("clientEducationDetail.comments.characterLimit", {
                count: COMMENT_MAX_LENGTH,
                remaining: Math.max(remainingChars, 0),
              })}
            </span>
          </div>
          <Button
            color="primary"
            disabled={isSubmitDisabled}
            loading={isSubmitting}
            onClick={handleSubmit}
            size="medium"
            variant="contained"
          >
            {t("clientEducationDetail.comments.submit")}
          </Button>
        </div>
      </div>

      {isLoading ? (
        <div className={styles.commentsEmptyState}>{t("common.loading")}</div>
      ) : items.length === 0 ? (
        <div className={styles.commentsEmptyState}>
          {t("clientEducationDetail.comments.empty")}
        </div>
      ) : (
        <div className={styles.commentsList}>
          {items.map((comment) => (
            <article key={comment.id} className={styles.commentCard}>
              <div className={styles.commentHeaderRow}>
                <div className={styles.commentAuthorBlock}>
                  <strong className={styles.commentAuthor}>
                    {getCommentAuthor(comment)}
                  </strong>
                  <span className={styles.commentDate}>
                    {formatDateLong(comment.createdAt)}
                  </span>
                </div>
              </div>
              <p className={styles.commentContent}>{comment.content}</p>
            </article>
          ))}
        </div>
      )}
    </section>
  );
}
